import Button from "common/Buttons/Button";
import { BOT_TYPE_TO_BASE_BACKTESTING_PATH } from "features/bots/common/constants";
import React, { CSSProperties } from "react";
import styled, { useTheme } from "styled-components";
import { ChevronRight } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import Text from "common/Typography/Text";
import { useHistory } from "react-router-dom";
import { BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

export type BacktestingBannerItem = "group" | "symbol" | "account" | "exchange";

interface SeeAllBacktestingsBannerProps {
  botType: BotType;
  selectedItems: BacktestingBannerItem[];
  hideSeeAllButton?: boolean;
  style?: CSSProperties;
}

const SeeAllBacktestingsBanner = ({
  botType,
  selectedItems,
  hideSeeAllButton,
  style,
}: SeeAllBacktestingsBannerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const history = useHistory();
  const { isNewLook } = useTheme();
  const seeAll = () => history.push(BOT_TYPE_TO_BASE_BACKTESTING_PATH[botType]);

  if (isNewLook) {
    // New look: a caption for the list under it, with a "See all" link like
    // a panel header's.
    return (
      <NewLookWrapper style={style}>
        <NewLookCaption>
          {t("backtesting.banner.showing_results", {
            item1: selectedItems[0],
            item2: selectedItems[1],
          })}
        </NewLookCaption>
        {!hideSeeAllButton && (
          <NewLookSeeAll type="button" onClick={seeAll}>
            {t("backtesting.banner.see_all")}
            <ChevronRight size={14} strokeWidth={2} />
          </NewLookSeeAll>
        )}
      </NewLookWrapper>
    );
  }

  return (
    <Wrapper style={style}>
      <Background>
        <StyledText>
          {t("backtesting.banner.showing_results", {
            item1: t(BANNER_ITEM_TO_DISPLAY_NAME[selectedItems[0]]),
            item2: t(BANNER_ITEM_TO_DISPLAY_NAME[selectedItems[1]]),
          })}
        </StyledText>
        {!hideSeeAllButton && (
          <Button $type="outlined" onClick={seeAll}>
            {t("backtesting.banner.see_all")}
          </Button>
        )}
      </Background>
    </Wrapper>
  );
};

export default SeeAllBacktestingsBanner;

const BANNER_ITEM_TO_DISPLAY_NAME: Record<
  BacktestingBannerItem,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  group: "backtesting.banner.items.group",
  symbol: "backtesting.banner.items.symbol",
  account: "backtesting.banner.items.account",
  exchange: "backtesting.banner.items.exchange",
};

const Wrapper = styled.div`
  width: 100%;
`;
const Background = styled.div`
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border-radius: ${(p) => (p.theme.isNewLook ? 12 : 8)}px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${(p) =>
    // New look: a plain caption above the table rather than a grey box.
    p.theme.isNewLook &&
    `
    background: none;
    border-radius: 0;
    padding: 0;
  `}
`;
const StyledText = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  border-left: 2px solid ${(p) => p.theme.colors.primaryBlue};
  padding: 0 12px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    border-left: none;
    padding: 0 12px 0 0;
    color: ${p.theme.colors.secondaryText};
    font-size: 12.5px;
  `}
`;
const NewLookWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;
const NewLookCaption = styled.span`
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookSeeAll = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  margin-right: -4px;
  padding: 0 4px 0 8px;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 120ms;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;
